import React, { useContext, useReducer } from "react"
import { CarrotErrorCodes } from "#/utils/CarrotErrors"
import { reportError } from "#/utils/ErrorReporting"
import { parseErrorBody } from "#/utils/HttpErrors"
import { useSelector } from "react-redux"
import { loginEmail } from "#/redux/reducers/loginInfo"
import { useSignUpFlow, LookupResult } from "../../context/signup/SignUpFlowContext"
import { useHistory } from "react-router"
import { useIntl } from "react-intl"
import { useTrackExperimentEvent } from "../../views/register/hooks/useExperiments"
import { carrotClient } from "#/utils/CarrotClient"

interface SignUpReducerState {
  username: string
  errorMessage: string
  inputsEnabled: boolean
  showSubmittingLabel: boolean
  success: boolean
  shake: boolean
}

type SignUpReducerAction = {
  type: string
  payload?: { errorCode: string }
}

interface SignUpProviderProps extends SignUpReducerState {
  onSubmit: ({ email }: { email: any }) => Promise<void>
}

const SignUpContext = React.createContext<SignUpProviderProps>(null)

export const useSignUp = () => useContext(SignUpContext)

const actionTypes = {
  error: "ERROR",
  start: "REQUEST_START",
  success: "REQUEST_SUCCESS"
}

function signUpReducer(state: SignUpReducerState, action: SignUpReducerAction): SignUpReducerState {
  switch (action.type) {
    case actionTypes.error: {
      return {
        ...state,
        inputsEnabled: true,
        showSubmittingLabel: false,
        shake: true,
        errorMessage: action.payload.errorCode
      }
    }
    case actionTypes.start: {
      return {
        ...state,
        inputsEnabled: true,
        showSubmittingLabel: true,
        shake: false
      }
    }
    case actionTypes.success: {
      return {
        ...state,
        inputsEnabled: false,
        showSubmittingLabel: false,
        success: true
      }
    }
    default: {
      throw new Error(`Unhandled type: ${action.type}`)
    }
  }
}

export function SignUpProvider({ children }: { children: React.ReactNode | React.ReactNode[] }) {
  const username = useSelector(loginEmail)
  const [state, dispatch] = useReducer(signUpReducer, {
    username,
    errorMessage: null,
    inputsEnabled: true,
    showSubmittingLabel: false,
    success: false,
    shake: false
  })
  const { lookupAccountByEmail } = useSignUpFlow()
  const history = useHistory()
  const intl = useIntl()
  const { locale } = intl
  const { trackExperimentEvent } = useTrackExperimentEvent()

  const setFormError = (errorCode: string): void => dispatch({ type: actionTypes.error, payload: { errorCode } })
  const startRequest = () => dispatch({ type: actionTypes.start })
  const endSuccessfulRequest = () => dispatch({ type: actionTypes.success })

  const setErrorState = (errorCode: string): void => {
    setFormError(errorCode)
  }

  const onSubmit = async ({ email }: { email: string }): Promise<void> => {
    trackExperimentEvent("sign-up-continue")
    startRequest()

    try {
      const lookupResult = await lookupAccountByEmail(email)
      switch (lookupResult.result) {
        case LookupResult.Proceed:
          // Continue to signUp
          break
        case LookupResult.Handled:
          return
        case LookupResult.Error:
          setFormError(lookupResult.errorCode)
          return
        default:
          // Unknown/future outcome → fail safe: surface an error, never auto-create the account.
          setFormError(CarrotErrorCodes.UNEXPECTED_ERROR)
          return
      }

      // Every submission that gets this far lands on the success page, which offers the
      // name-and-date-of-birth form as a click-through for members whose email we do not hold.
      await carrotClient.signUp(email, locale)
    } catch (error) {
      // parseErrorBody answers null rather than throwing when the body is absent or not JSON,
      // which a gateway error page or an empty 4xx will be.
      const code = (await parseErrorBody(error))?.code

      if (!code) {
        reportError(error)
        setErrorState(CarrotErrorCodes.UNEXPECTED_ERROR)
        return
      }

      // A backend that still answers differently for an address matching no member returns 404
      // here. Fall through to the success page so this screen is the same either way, rather
      // than dead-ending the roughly half of submissions that resolve to nobody.
      if (code !== CarrotErrorCodes.EMPLOYEE_NOT_FOUND) {
        setErrorState(code)
        return
      }
    }
    endSuccessfulRequest()
    const signupSuccessHref = `/signup-success?email=${encodeURIComponent(email)}`
    history.push(signupSuccessHref)
  }

  return <SignUpContext.Provider value={{ ...state, onSubmit }}>{children}</SignUpContext.Provider>
}
